import React from "react";
import { Table, Tag, Typography, Tooltip, Switch, Space, Button, Popconfirm } from "antd";
import { EditOutlined, DeleteOutlined, ExperimentOutlined } from "@ant-design/icons";

const { Text } = Typography;

const PROVIDER_COLORS = {
  openai: "blue",
  anthropic: "purple",
  gemini: "green",
  openrouter: "orange",
};

const formatLabel = (name) =>
  name?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "-";

const AiProviderTable = ({ configs, loading, onEdit, onDelete, onToggle, togglingKey, onPlayground }) => {
  const columns = [
    {
      title: "Feature",
      dataIndex: "feature_name",
      key: "feature_name",
      width: 200,
      render: (val) => <Text strong>{formatLabel(val)}</Text>,
    },
    {
      title: "Provider",
      dataIndex: "provider",
      key: "provider",
      width: 120,
      render: (val) => (
        <Tag color={PROVIDER_COLORS[val] || "default"} style={{ textTransform: "capitalize" }}>
          {val}
        </Tag>
      ),
    },
    {
      title: "Model",
      dataIndex: "model_version",
      key: "model_version",
      width: 200,
      render: (val) => (
        <Tooltip title={val}>
          <Text
            style={{
              maxWidth: 180,
              display: "inline-block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {val}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Temp",
      dataIndex: "temperature",
      key: "temperature",
      width: 70,
      render: (val) => <Text>{val ?? "-"}</Text>,
    },
    {
      title: "Max Tokens",
      dataIndex: "max_tokens",
      key: "max_tokens",
      width: 100,
      render: (val) => <Text>{val?.toLocaleString() ?? "-"}</Text>,
    },
    {
      title: "Top P",
      dataIndex: "top_p",
      key: "top_p",
      width: 70,
      render: (val) => <Text>{val ?? "-"}</Text>,
    },
    {
      title: "Freq. Penalty",
      dataIndex: "frequency_penalty",
      key: "frequency_penalty",
      width: 110,
      render: (val) => <Text>{val ?? "-"}</Text>,
    },
    {
      title: "Pres. Penalty",
      dataIndex: "presence_penalty",
      key: "presence_penalty",
      width: 110,
      render: (val) => <Text>{val ?? "-"}</Text>,
    },
    {
      title: "Enabled",
      dataIndex: "enabled",
      key: "enabled",
      width: 80,
      render: (val, record) => (
        <Switch
          checked={val}
          size="small"
          loading={togglingKey === record.feature_name}
          onChange={() => onToggle(record)}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 136,
      fixed: "right",
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => onEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Playground">
            <Button
              icon={<ExperimentOutlined />}
              size="small"
              onClick={() => onPlayground(record)}
            />
          </Tooltip>
          <Popconfirm
            title={`Delete "${record.feature_name}" configuration?`}
            description="This action cannot be undone."
            okText="Delete"
            okType="danger"
            cancelText="Cancel"
            onConfirm={() => onDelete(record)}
          >
            <Tooltip title="Delete">
              <Button danger icon={<DeleteOutlined />} size="small" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={configs}
      loading={loading}
      rowKey={(r) => r._id || r.id || `${r.feature_name}-${r.provider}`}
      pagination={{ pageSize: 10, showSizeChanger: false }}
      scroll={{ x: 1200 }}
      size="middle"
    />
  );
};

export default AiProviderTable;

